'use client';
import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Building2, User, Tag, Lock, ChevronRight, Info, Plus, Trash2, Package, Palette, DollarSign, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { addListing, addProduct } from '@/lib/storage';
import GradientColorPicker from '@/components/ui/GradientColorPicker';

interface FormValues {
  businessName: string;
  description: string;
  category: string;
  ownerName: string;
  pin: string;
  confirmPin: string;
}

interface ProductDraft {
  draftId: string;
  name: string;
  price: string;
  imageFile: File | null;
  imagePreview: string;
  description: string;
}

function ProductDraftRow({
  draft,
  index,
  onChange,
  onRemove,
}: {
  draft: ProductDraft;
  index: number;
  onChange: (id: string, field: keyof Omit<ProductDraft, 'draftId'>, value: string | File | null) => void;
  onRemove: (id: string) => void;
}) {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file) {
      const preview = URL.createObjectURL(file);
      onChange(draft.draftId, 'imageFile', file);
      onChange(draft.draftId, 'imagePreview', preview);
    }
  };

  return (
    <div className="bg-zinc-800/60 border border-zinc-700 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">Product {index + 1}</span>
        <button
          type="button"
          onClick={() => onRemove(draft.draftId)}
          className="p-1 text-zinc-500 hover:text-red-400 transition-colors duration-150"
          aria-label="Remove product"
        >
          <Trash2 size={14} />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1">Product Name *</label>
          <input
            type="text"
            value={draft.name}
            onChange={(e) => onChange(draft.draftId, 'name', e.target.value)}
            placeholder="e.g. Wireless Headphones"
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all duration-150"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1">Price (USD) *</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={draft.price}
            onChange={(e) => onChange(draft.draftId, 'price', e.target.value)}
            placeholder="e.g. 29.99"
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all duration-150"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-zinc-400 mb-1">Product Image</label>
          <div className="flex items-center gap-3">
            {draft.imagePreview && (
              <img src={draft.imagePreview} alt="Product preview" className="w-14 h-14 object-cover rounded-lg border border-zinc-700 flex-shrink-0" />
            )}
            <label className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-zinc-800 border border-dashed border-zinc-600 hover:border-blue-500/60 rounded-lg cursor-pointer transition-all duration-150 text-sm text-zinc-400 hover:text-zinc-200">
              <Plus size={14} />
              {draft.imagePreview ? 'Change Image' : 'Choose Image File'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-zinc-400 mb-1">Description *</label>
          <textarea
            rows={2}
            value={draft.description}
            onChange={(e) => onChange(draft.draftId, 'description', e.target.value)}
            placeholder="Describe this product..."
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all duration-150 resize-none"
          />
        </div>
      </div>
    </div>
  );
}

function PaymentModal({
  open,
  onAgree,
  onDecline,
}: {
  open: boolean;
  onAgree: () => void;
  onDecline: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onDecline} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 bg-green-500/10 rounded-xl">
            <DollarSign size={20} className="text-green-400" />
          </div>
          <h2 className="text-lg font-semibold text-zinc-100">Listing Fee</h2>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed mb-2">
          To post your business on <span className="text-white font-semibold">GigaMarketplace</span>, there is a weekly fee of:
        </p>
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3 mb-5 text-center">
          <span className="text-2xl font-bold text-green-400">$3</span>
          <span className="text-sm text-green-300 ml-1">/ week</span>
          <p className="text-xs text-zinc-400 mt-1">Paid to <span className="text-zinc-200 font-medium">Silas</span> to keep your listing active</p>
        </div>
        <p className="text-xs text-zinc-500 mb-6 leading-relaxed">
          Are you willing to pay $3 a week to Silas to have your business listed here?
        </p>
        <div className="flex gap-3">
          <button
            onClick={onAgree}
            className="flex-1 py-2.5 bg-green-600 hover:bg-green-500 active:scale-95 text-white font-semibold text-sm rounded-xl transition-all duration-150"
          >
            Yes, I Agree
          </button>
          <button
            onClick={onDecline}
            className="flex-1 py-2.5 text-sm font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded-xl transition-all duration-150"
          >
            No, Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AddBusinessForm() {
  const router = useRouter();
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [productDrafts, setProductDrafts] = useState<ProductDraft[]>([]);
  const [selectedColor, setSelectedColor] = useState('#2563eb');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<FormValues | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [mounted, setMounted] = useState(false);
  const draftCounter = React.useRef(0);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onBlur' });

  const pinValue = watch('pin');

  const addProductDraft = () => {
    draftCounter.current += 1;
    setProductDrafts((prev) => [
      ...prev,
      { draftId: `draft-${draftCounter.current}`, name: '', price: '', imageFile: null, imagePreview: '', description: '' },
    ]);
  };

  const updateProductDraft = (id: string, field: keyof Omit<ProductDraft, 'draftId'>, value: string | File | null) => {
    setProductDrafts((prev) =>
      prev.map((d) => (d.draftId === id ? { ...d, [field]: value } : d))
    );
  };

  const removeProductDraft = (id: string) => {
    setProductDrafts((prev) => prev.filter((d) => d.draftId !== id));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
  };

  const onSubmit = async (data: FormValues) => {
    for (const draft of productDrafts) {
      if (!draft.name.trim() || !draft.description.trim() || !draft.price) {
        toast.error('Please fill in all required product fields (name, price, description).');
        return;
      }
      if (isNaN(parseFloat(draft.price)) || parseFloat(draft.price) < 0) {
        toast.error('Please enter a valid price for all products.');
        return;
      }
    }
    setPendingFormData(data);
    setPaymentModalOpen(true);
  };

  const handlePaymentAgree = async () => {
    if (!pendingFormData) return;
    const data = pendingFormData;
    setPaymentModalOpen(false);
    setSubmitting(true);
    try {
      // On iOS, logo/image uploads can fail due to HEIC format or MIME type issues.
      // We attempt the upload but fall back to no logo so the listing still succeeds.
      let safeLogoFile: File | null = logoFile;
      if (safeLogoFile) {
        // Validate the file is still accessible (iOS can revoke File references)
        try {
          await safeLogoFile.arrayBuffer();
        } catch {
          safeLogoFile = null;
        }
      }

      // Add listing — saved as 'pending' until admin approves
      let newListing;
      try {
        newListing = await addListing({
          businessName: data.businessName.trim(),
          description: data.description.trim(),
          category: data.category,
          ownerName: data.ownerName.trim(),
          pin: data.pin,
          color: selectedColor,
          logoFile: safeLogoFile,
        });
      } catch (logoErr) {
        // If upload failed (e.g. iOS HEIC rejection), retry without the logo
        if (safeLogoFile) {
          newListing = await addListing({
            businessName: data.businessName.trim(),
            description: data.description.trim(),
            category: data.category,
            ownerName: data.ownerName.trim(),
            pin: data.pin,
            color: selectedColor,
            logoFile: null,
          });
        } else {
          throw logoErr;
        }
      }

      // Save products — images uploaded to Supabase Storage inside addProduct
      const savedProductIds: string[] = [];
      for (const draft of productDrafts) {
        let safeImageFile: File | null = draft.imageFile;
        try {
          if (safeImageFile) await safeImageFile.arrayBuffer();
        } catch {
          safeImageFile = null;
        }
        let savedProduct;
        try {
          savedProduct = await addProduct({
            businessId: newListing.id,
            name: draft.name.trim(),
            description: draft.description.trim(),
            price: parseFloat(draft.price),
            imageUrl: '',
            imageFile: safeImageFile,
          });
        } catch {
          // If product image upload fails on iOS, retry without image
          if (safeImageFile) {
            savedProduct = await addProduct({
              businessId: newListing.id,
              name: draft.name.trim(),
              description: draft.description.trim(),
              price: parseFloat(draft.price),
              imageUrl: '',
              imageFile: null,
            });
          }
        }
        if (savedProduct) savedProductIds.push(savedProduct.id);
      }

      // Track submission in localStorage so the owner can see it was submitted
      try {
        const submitted: string[] = JSON.parse(localStorage.getItem('giga_submitted_businesses') || '[]');
        submitted.push(newListing.id);
        localStorage.setItem('giga_submitted_businesses', JSON.stringify(submitted));

        // Store full listing data so owner can preview it before approval
        const myBusinesses: Record<string, unknown> = JSON.parse(localStorage.getItem('giga_my_businesses') || '{}');
        myBusinesses[newListing.id] = newListing;
        localStorage.setItem('giga_my_businesses', JSON.stringify(myBusinesses));

        // Store pending product IDs for this business so owner can see them
        if (savedProductIds.length > 0) {
          const myProducts: Record<string, string[]> = JSON.parse(localStorage.getItem('giga_my_products') || '{}');
          myProducts[newListing.id] = [...(myProducts[newListing.id] || []), ...savedProductIds];
          localStorage.setItem('giga_my_products', JSON.stringify(myProducts));
        }
      } catch {
        // ignore localStorage errors
      }

      toast.success(`"${data.businessName}" has been added successfully!`);
      router.push('/business-listings');
    } catch {
      toast.error('Failed to add listing. Please try again.');
      setSubmitting(false);
    }
  };

  const handlePaymentDecline = () => {
    setPaymentModalOpen(false);
    setPendingFormData(null);
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-12">
      {!mounted ? (
        <div className="max-w-2xl mx-auto">
          <div className="mb-10">
            <div className="flex items-center gap-2 text-sm text-zinc-500 mb-4">
              <a href="/business-listings" className="hover:text-zinc-300 transition-colors duration-150">
                Directory
              </a>
              <ChevronRight size={14} />
              <span className="text-zinc-300">Add Business</span>
            </div>
            <h1 className="text-3xl font-bold text-zinc-100 tracking-tight mb-2">
              List Your Business
            </h1>
            <p className="text-zinc-500 text-sm leading-relaxed">
              Add your business to GigaMarketplace. No account needed — set a private PIN to protect your listing.
            </p>
          </div>
          <div className="flex items-center justify-center py-20">
            <svg className="animate-spin h-6 w-6 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        </div>
      ) : (
      <div className="max-w-2xl mx-auto">
        <div className="mb-10">
          <div className="flex items-center gap-2 text-sm text-zinc-500 mb-4">
            <a href="/business-listings" className="hover:text-zinc-300 transition-colors duration-150">
              Directory
            </a>
            <ChevronRight size={14} />
            <span className="text-zinc-300">Add Business</span>
          </div>
          <h1 className="text-3xl font-bold text-zinc-100 tracking-tight mb-2">
            List Your Business
          </h1>
          <p className="text-zinc-500 text-sm leading-relaxed">
            Add your business to GigaMarketplace. No account needed — set a private PIN to protect your listing.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Business Info */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-6">
              <Building2 size={18} className="text-blue-400" />
              <h2 className="text-base font-semibold text-zinc-200">Business Information</h2>
            </div>

            <div className="mb-5">
              <label htmlFor="businessName" className="block text-sm font-medium text-zinc-300 mb-1.5">
                Business Name <span className="text-red-400">*</span>
              </label>
              <input
                id="businessName"
                type="text"
                placeholder="e.g. Apex Tech Solutions"
                className={`w-full bg-zinc-800 border ${errors.businessName ? 'border-red-500' : 'border-zinc-700'} rounded-xl px-4 py-2.5 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all duration-150`}
                {...register('businessName', {
                  required: 'Business name is required',
                  minLength: { value: 2, message: 'Name must be at least 2 characters' },
                  maxLength: { value: 80, message: 'Name must be under 80 characters' },
                })}
              />
              {errors.businessName && (
                <p className="mt-1.5 text-xs text-red-400">{errors.businessName.message}</p>
              )}
            </div>

            <div className="mb-5">
              <label htmlFor="description" className="block text-sm font-medium text-zinc-300 mb-1.5">
                Description <span className="text-red-400">*</span>
              </label>
              <p className="text-xs text-zinc-500 mb-2">
                Tell people what your business does, what you offer, and who you serve.
              </p>
              <textarea
                id="description"
                rows={4}
                placeholder="We provide high-quality products and services to customers across the region..."
                className={`w-full bg-zinc-800 border ${errors.description ? 'border-red-500' : 'border-zinc-700'} rounded-xl px-4 py-2.5 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all duration-150 resize-none`}
                {...register('description', {
                  required: 'Description is required',
                  minLength: { value: 20, message: 'Please write at least 20 characters' },
                  maxLength: { value: 600, message: 'Description must be under 600 characters' },
                })}
              />
              {errors.description && (
                <p className="mt-1.5 text-xs text-red-400">{errors.description.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-zinc-300 mb-1.5">
                <span className="flex items-center gap-1.5">
                  <Tag size={13} />
                  Category / Industry <span className="text-red-400">*</span>
                </span>
              </label>
              <input
                type="text"
                id="category"
                placeholder="e.g. Technology, Retail, Food & Beverage..."
                className={`w-full bg-zinc-800 border ${errors.category ? 'border-red-500' : 'border-zinc-700'} rounded-xl px-4 py-2.5 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all duration-150`}
                {...register('category', { required: 'Please enter a category' })}
              />
              {errors.category && (
                <p className="mt-1.5 text-xs text-red-400">{errors.category.message}</p>
              )}
            </div>
          </div>

          {/* Brand Color */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Palette size={18} className="text-blue-400" />
              <h2 className="text-base font-semibold text-zinc-200">Business Card Color</h2>
            </div>
            <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
              Choose an accent color for your business card on the homepage.
            </p>
            <GradientColorPicker value={selectedColor} onChange={setSelectedColor} />
          </div>

          {/* Business Logo */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <ImageIcon size={18} className="text-blue-400" />
              <h2 className="text-base font-semibold text-zinc-200">Business Logo <span className="text-zinc-500 font-normal text-xs ml-1">(optional)</span></h2>
            </div>
            <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
              Upload a logo to display on your business card in the directory.
            </p>
            <div className="flex items-center gap-4">
              {logoPreview && (
                <div className="relative flex-shrink-0">
                  <img src={logoPreview} alt="Logo preview" className="w-16 h-16 object-contain rounded-xl border border-zinc-700 bg-zinc-800" />
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 hover:bg-red-400 rounded-full flex items-center justify-center text-white transition-colors"
                    aria-label="Remove logo"
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>
                </div>
              )}
              <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-zinc-800 border border-dashed border-zinc-600 hover:border-blue-500/60 rounded-lg cursor-pointer transition-all duration-150 text-sm text-zinc-400 hover:text-zinc-200">
                <ImageIcon size={15} />
                {logoPreview ? 'Change Logo' : 'Choose Logo File'}
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
              </label>
            </div>
          </div>

          {/* Owner Info */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-6">
              <User size={18} className="text-blue-400" />
              <h2 className="text-base font-semibold text-zinc-200">Business Owner</h2>
            </div>
            <div>
              <label htmlFor="ownerName" className="block text-sm font-medium text-zinc-300 mb-1.5">
                Owner Name <span className="text-red-400">*</span>
              </label>
              <p className="text-xs text-zinc-500 mb-2">
                Your name as the person responsible for this listing.
              </p>
              <input
                id="ownerName"
                type="text"
                placeholder="e.g. Jordan Smith"
                className={`w-full bg-zinc-800 border ${errors.ownerName ? 'border-red-500' : 'border-zinc-700'} rounded-xl px-4 py-2.5 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all duration-150`}
                {...register('ownerName', {
                  required: 'Owner name is required',
                  minLength: { value: 2, message: 'Name must be at least 2 characters' },
                  maxLength: { value: 60, message: 'Name must be under 60 characters' },
                })}
              />
              {errors.ownerName && (
                <p className="mt-1.5 text-xs text-red-400">{errors.ownerName.message}</p>
              )}
            </div>
          </div>

          {/* Products */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Package size={18} className="text-blue-400" />
                <h2 className="text-base font-semibold text-zinc-200">Products</h2>
              </div>
              <span className="text-xs text-zinc-500">{productDrafts.length} added</span>
            </div>
            <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
              Add products to your store. You can also add more products later by editing your listing.
            </p>

            {productDrafts.map((draft, index) => (
              <ProductDraftRow
                key={draft.draftId}
                draft={draft}
                index={index}
                onChange={updateProductDraft}
                onRemove={removeProductDraft}
              />
            ))}

            <button
              type="button"
              onClick={addProductDraft}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-blue-400 hover:text-blue-300 border border-dashed border-blue-500/40 hover:border-blue-400/60 hover:bg-blue-500/5 rounded-xl transition-all duration-150"
            >
              <Plus size={15} />
              Add a Product
            </button>
          </div>

          {/* PIN Setup */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 mb-8">
            <div className="flex items-center gap-2 mb-2">
              <Lock size={18} className="text-blue-400" />
              <h2 className="text-base font-semibold text-zinc-200">Set Your Private PIN</h2>
            </div>
            <div className="flex items-start gap-2 bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-3 mb-6">
              <Info size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-300 leading-relaxed">
                Your PIN is private — only someone with this code can edit or delete your listing later. There is no way to recover a lost PIN, so remember it.
              </p>
            </div>

            <div className="mb-5">
              <label htmlFor="pin" className="block text-sm font-medium text-zinc-300 mb-1.5">
                PIN <span className="text-red-400">*</span>
              </label>
              <p className="text-xs text-zinc-500 mb-2">
                At least 4 characters. Use letters, numbers, or symbols.
              </p>
              <div className="relative">
                <input
                  id="pin"
                  type={showPin ? 'text' : 'password'}
                  placeholder="Create a PIN..."
                  autoComplete="new-password"
                  className={`w-full bg-zinc-800 border ${errors.pin ? 'border-red-500' : 'border-zinc-700'} rounded-xl px-4 py-2.5 pr-12 text-sm font-mono text-zinc-100 placeholder-zinc-600 placeholder:font-sans focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all duration-150`}
                  {...register('pin', {
                    required: 'PIN is required',
                    minLength: { value: 4, message: 'PIN must be at least 4 characters' },
                    maxLength: { value: 32, message: 'PIN must be under 32 characters' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPin((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-zinc-300 transition-colors duration-150"
                  aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                >
                  {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.pin && (
                <p className="mt-1.5 text-xs text-red-400">{errors.pin.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPin" className="block text-sm font-medium text-zinc-300 mb-1.5">
                Confirm PIN <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  id="confirmPin"
                  type={showConfirmPin ? 'text' : 'password'}
                  placeholder="Repeat your PIN..."
                  autoComplete="new-password"
                  className={`w-full bg-zinc-800 border ${errors.confirmPin ? 'border-red-500' : 'border-zinc-700'} rounded-xl px-4 py-2.5 pr-12 text-sm font-mono text-zinc-100 placeholder-zinc-600 placeholder:font-sans focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all duration-150`}
                  {...register('confirmPin', {
                    required: 'Please confirm your PIN',
                    validate: (val) => val === pinValue || 'PINs do not match',
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPin((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-zinc-300 transition-colors duration-150"
                  aria-label={showConfirmPin ? 'Hide PIN' : 'Show PIN'}
                >
                  {showConfirmPin ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.confirmPin && (
                <p className="mt-1.5 text-xs text-red-400">{errors.confirmPin.message}</p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 py-3.5 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 disabled:cursor-not-allowed active:scale-95 text-white font-semibold rounded-xl transition-all duration-150 shadow-lg shadow-blue-900/20"
          >
            {submitting ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Submitting...
              </>
            ) : (
              <>
                <Building2 size={16} />
                List My Business
              </>
            )}
          </button>
        </form>
      </div>
      )}

      <PaymentModal
        open={paymentModalOpen}
        onAgree={handlePaymentAgree}
        onDecline={handlePaymentDecline}
      />
    </div>
  );
}